// src/components/Navbar.js
import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import logo from "../assets/logo.webp";
import AuthModal from "./AuthModal";
import Toast from "./Toast";
import ConfirmationModal from "./ConfirmationModal";
import { useUser } from "../context/UserContext";

function Navbar() {
  const { user, setUser, signOut, showAuthModal, closeLoginModal, openLoginModal } =
    useUser();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.toggle("no-scroll", isMenuOpen);
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const closeDropdown = () => setIsDropdownOpen(false);
    if (isDropdownOpen) {
      window.addEventListener("click", closeDropdown);
    }
    return () => window.removeEventListener("click", closeDropdown);
  }, [isDropdownOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    closeLoginModal();
    setToastMessage(`Welcome, ${userData.first_name || userData.phone}!`);
  };

  const handleAccountClick = (e) => {
    if (user) {
      e.stopPropagation();
      setIsDropdownOpen(!isDropdownOpen);
    } else {
      openLoginModal();
    }
  };

  return (
    <>
      <nav className={`font-prata flex justify-between items-center standard-padding py-5 bg-black fixed top-0 w-full z-[120] tracking-[0.08em] font-medium text-[14px] capitalize max-lg:flex-wrap max-lg:py-3.5 max-[480px]:py-3 border-b border-white/5 transition-shadow duration-300 ${isScrolled ? "shadow-[0_3px_8px_rgba(0,0,0,0.25)]" : "shadow-none"}`}>
        <NavLink to="/" onClick={closeMenu} className="flex items-center">
          <img
            src={logo}
            alt="logo"
            className="w-[150px] h-[60px] object-contain max-lg:w-[130px] max-lg:h-[55px] max-[480px]:w-[130px] max-[480px]:h-[52px] block"
          />
        </NavLink>

        {/* Mobile right-side controls: account icon + hamburger */}
        <div className="hidden max-lg:flex items-center gap-5 relative z-[130] shrink-0">
          {/* Account button — hide on mobile when menu is open */}
          <button
            onClick={handleAccountClick}
            aria-label={user ? "Go to profile" : "Log in"}
            className={`${isMenuOpen ? "hidden" : "flex"} items-center gap-1.5 px-4 max-[380px]:px-0 max-[380px]:w-9 max-[380px]:justify-center h-9 rounded-full bg-[#fa9324] text-black text-[14px] font-bold shadow-[0_4px_12px_rgba(250,147,36,0.3)] transition hover:bg-[#e07010] shrink-0`}
          >
            <i className="fas fa-user text-[12px] max-[380px]:text-[16px]" />
            <span className="max-w-[70px] truncate max-[380px]:hidden">
              {user ? user.first_name || "Account" : "Log in"}
            </span>
          </button>

          {/* Mobile Dropdown Menu */}
          {user && isDropdownOpen && !isMenuOpen && (
            <div 
              className="absolute right-[45px] top-12 w-[200px] rounded-2xl border border-[#222] bg-[#111] shadow-[0_12px_32px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col z-[200] animate-slide-down"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-2 border-b border-[#222] bg-black/40">
                <p className="text-[9px] text-[#777] uppercase tracking-wider font-bold">Signed in as</p>
                <p className="text-[12px] text-white font-bold truncate">{user.first_name || "User"}</p>
              </div>
              <button
                onClick={() => { navigate("/profile?tab=loyalty"); setIsDropdownOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 text-[13px] text-white hover:text-brand-orange hover:bg-neutral-900 transition-colors text-left font-semibold border-b border-[#222]/50"
              >
                <i className="fas fa-coins text-[#fa9324] w-4" />
                Loyalty Points
              </button>
              <button
                onClick={() => { navigate("/profile?tab=profile"); setIsDropdownOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 text-[13px] text-white hover:text-brand-orange hover:bg-neutral-900 transition-colors text-left font-semibold border-b border-[#222]/50"
              >
                <i className="fas fa-user text-[#fa9324] w-4" />
                Profile Details
              </button>
              <button
                onClick={() => { navigate("/profile?tab=orders"); setIsDropdownOpen(false); }}
                className="flex items-center gap-3 px-4 py-3 text-[13px] text-white hover:text-brand-orange hover:bg-neutral-900 transition-colors text-left font-semibold border-b border-[#222]/50"
              >
                <i className="fas fa-receipt text-[#fa9324] w-4" />
                Order History
              </button>
              <button
                onClick={() => { 
                  setIsDropdownOpen(false); 
                  setShowLogoutConfirm(true); 
                }}
                className="flex items-center gap-3 px-4 py-3 text-[13px] text-red-400 hover:bg-red-500/10 transition-colors text-left font-semibold"
              >
                <i className="fas fa-sign-out-alt w-4 text-red-500" />
                Sign Out
              </button>
            </div>
          )}

          {/* Hamburger */}
          <div className="cursor-pointer flex items-center" onClick={toggleMenu}>
            {isMenuOpen ? (
              <i className="fas fa-times text-xl text-white" />
            ) : (
              <div className="w-[26px] h-[18px] relative flex flex-col justify-between">
                <span className="block h-[2px] bg-white rounded-sm transition-all duration-300 w-[18px] self-start" />
                <span className="block h-[2px] bg-white rounded-sm transition-all duration-300 w-[26px]" />
                <span className="block h-[2px] bg-white rounded-sm transition-all duration-300 w-[18px] self-start" />
              </div>
            )}
          </div>
        </div>

        {/* Overlay for mobile menu */}
        <div
          className={`fixed inset-0 bg-[rgba(70,66,66,0.8)] z-[100] transition-opacity duration-300 ${isMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
          onClick={closeMenu}
        />

        {/* Nav links */}
        <ul
          className={`list-none flex items-center gap-[30px] max-lg:flex-col max-lg:items-start max-lg:absolute max-lg:top-0 max-lg:right-0 max-lg:bg-black max-lg:z-[120] max-lg:text-left max-lg:px-[60px] max-lg:pb-[25px] max-lg:pt-[90px] max-lg:h-screen max-lg:w-auto ${isMenuOpen ? "max-lg:flex" : "max-lg:hidden"}`}
        >
          <li>
            <NavLink
              to="/"
              end
              onClick={closeMenu}
              className={({ isActive }) =>
                `no-underline cursor-pointer ${isActive ? "text-brand-orange" : "text-white"} hover:text-brand-orange transition-colors`
              }
            >
              Home
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/order-online"
              onClick={closeMenu}
              className={({ isActive }) =>
                `no-underline cursor-pointer ${isActive ? "text-brand-orange" : "text-white"} hover:text-brand-orange transition-colors`
              }
            >
              Order
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/buffet"
              onClick={closeMenu}
              className={({ isActive }) =>
                `no-underline cursor-pointer ${isActive ? "text-brand-orange" : "text-white"} hover:text-brand-orange transition-colors`
              }
            >
              Buffet Reservations
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/private-events"
              onClick={closeMenu}
              className={({ isActive }) =>
                `no-underline cursor-pointer ${isActive ? "text-brand-orange" : "text-white"} hover:text-brand-orange transition-colors`
              }
            >
              Event Halls
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/contact"
              onClick={closeMenu}
              className={({ isActive }) =>
                `no-underline cursor-pointer ${isActive ? "text-brand-orange" : "text-white"} hover:text-brand-orange transition-colors`
              }
            >
              Contact
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/blog"
              onClick={closeMenu}
              className={({ isActive }) =>
                `no-underline cursor-pointer ${isActive ? "text-brand-orange" : "text-white"} hover:text-brand-orange transition-colors`
              }
            >
              Blog
            </NavLink>
          </li>

          {/* Desktop: account button in nav bar */}
          <li className="relative max-lg:hidden">
            <button
              onClick={handleAccountClick}
              aria-label={user ? "Go to profile" : "Log in"}
              className="flex items-center gap-2 px-4 h-10 rounded-full bg-[#fa9324] text-black text-[13px] font-bold shadow-[0_4px_12px_rgba(250,147,36,0.2)] transition hover:bg-[#e07010] cursor-pointer"
            >
              <i className="fas fa-user text-[11px]" />
              <span className="max-w-[100px] truncate">
                {user ? user.first_name || "Account" : "Log in"}
              </span>
            </button>

            {/* Desktop Dropdown Menu */}
            {user && isDropdownOpen && (
              <div 
                className="absolute right-0 top-12 w-[220px] rounded-2xl border border-[#222] bg-[#111] shadow-[0_12px_32px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col z-[200] animate-slide-down"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-4 py-2.5 border-b border-[#222] bg-black/40">
                  <p className="text-[10px] text-[#777] uppercase tracking-wider font-bold">Signed in as</p>
                  <p className="text-[13px] text-white font-bold truncate">{user.first_name || "User"}</p>
                </div>
                <button
                  onClick={() => { navigate("/profile?tab=loyalty"); setIsDropdownOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3.5 text-[13px] text-white hover:text-brand-orange hover:bg-neutral-900 transition-colors text-left font-semibold border-b border-[#222]/50 cursor-pointer"
                >
                  <i className="fas fa-coins text-[#fa9324] w-4" />
                  Loyalty Points
                </button>
                <button
                  onClick={() => { navigate("/profile?tab=profile"); setIsDropdownOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3.5 text-[13px] text-white hover:text-brand-orange hover:bg-neutral-900 transition-colors text-left font-semibold border-b border-[#222]/50 cursor-pointer"
                >
                  <i className="fas fa-user text-[#fa9324] w-4" />
                  Profile Details
                </button>
                <button
                  onClick={() => { navigate("/profile?tab=orders"); setIsDropdownOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3.5 text-[13px] text-white hover:text-brand-orange hover:bg-neutral-900 transition-colors text-left font-semibold cursor-pointer border-b border-[#222]/50"
                >
                  <i className="fas fa-receipt text-[#fa9324] w-4" />
                  Order History
                </button>
                <button
                  onClick={() => { 
                    setIsDropdownOpen(false); 
                    setShowLogoutConfirm(true); 
                  }}
                  className="flex items-center gap-3 px-4 py-3.5 text-[13px] text-red-400 hover:bg-red-500/10 transition-colors text-left font-semibold cursor-pointer"
                >
                  <i className="fas fa-sign-out-alt w-4 text-red-500" />
                  Sign Out
                </button>
              </div>
            )}
          </li>


        </ul>
      </nav>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          onClose={closeLoginModal}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {/* Toast */}
      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => setToastMessage("")}
          duration={3000}
        />
      )}

      {/* Logout confirmation modal */}
      <ConfirmationModal
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={() => {
          signOut();
          setShowLogoutConfirm(false);
          navigate("/", { replace: true });
        }}
        title="Sign Out"
        message="Are you sure you want to sign out of your account?"
        confirmText="Yes, Sign Out"
      />
    </>
  );
}

export default Navbar;
